import { dayjs, toUtcAdjusted } from '@noor/lib';
import type { ClientListSearch } from '../model/list-params';
import type { BusinessType, ClientType, VendorStatusFilter } from '../model/schema';
import { CLIENT_SORT_FIELDS, type ClientSortField } from './labels';

/**
 * Wire filter for the legacy list endpoints (ported 1:1 from cpanel
 * `features/clients/model/filters.ts:buildClientsListFilter`, including the
 * UTC+5 date adjustment and the 31-day default orders window). The only
 * deliberate change: pagination arrives as page/pageSize and is converted
 * to the wire `limit`/`skip` here, at the boundary.
 */
export interface ClientsListFilter {
  limit: number;
  skip: number;
  search?: string | undefined;
  is_business?: boolean | undefined;
  is_corporate?: boolean | undefined;
  order_by_created_at?: boolean | undefined;
  order_by_order_price?: boolean | undefined;
  order_by_order_count?: boolean | undefined;
  order_by_balance?: boolean | undefined;
  order_by_balance_warning?: boolean | undefined;
  order_by_average_cheque?: boolean | undefined;
  orders_from_date: string;
  orders_till_date: string;
  order_price_from?: string | undefined;
  order_price_till?: string | undefined;
  order_count_from?: string | undefined;
  order_count_till?: string | undefined;
  balance_from?: string | undefined;
  balance_till?: string | undefined;
  tariff_id?: string | undefined;
  sales_manager_id?: string | undefined;
  status?: VendorStatusFilter | undefined;
}

export interface ClientSort {
  field: ClientSortField;
  /** 1 = ascending (wire `true`), 2 = descending (wire `false`). */
  position: 1 | 2;
}

/** Parse the URL `csort` value; unknown fields/positions mean "no sort". */
export function parseClientSort(raw: string | undefined): ClientSort | null {
  if (!raw) return null;
  const [field, position] = raw.split(':');
  if (!(CLIENT_SORT_FIELDS as readonly string[]).includes(field ?? '')) return null;
  if (position !== '1' && position !== '2') return null;
  return { field: field as ClientSortField, position: Number(position) as 1 | 2 };
}

export function serializeClientSort(sort: ClientSort | null): string | undefined {
  return sort ? `${sort.field}:${sort.position}` : undefined;
}

/**
 * Header click cycle (cpanel `CustomHeader`): none → 1 → 2 → none, and
 * choosing a field clears every other field (single active sort).
 */
export function nextClientSort(
  current: ClientSort | null,
  field: ClientSortField,
): ClientSort | null {
  if (current?.field !== field) return { field, position: 1 };
  if (current.position === 1) return { field, position: 2 };
  return null;
}

const SORT_WIRE_KEY: Record<ClientSortField, keyof ClientsListFilter> = {
  created_date: 'order_by_created_at',
  total_order_price: 'order_by_order_price',
  orders_count: 'order_by_order_count',
  balance: 'order_by_balance',
  diff_balance: 'order_by_balance_warning',
  average_cheque: 'order_by_average_cheque',
};

export function buildClientsListFilter(args: {
  values: Partial<ClientListSearch>;
  clientType: ClientType;
  businessType: BusinessType;
  sort: ClientSort | null;
}): ClientsListFilter {
  const { values, clientType, businessType, sort } = args;
  const page = values.cpage ?? 1;
  const pageSize = values.cpageSize ?? 10;
  const filter: ClientsListFilter = {
    limit: pageSize,
    skip: (page - 1) * pageSize,
    search: values.csearch,
    is_business:
      clientType === 'business-vendor'
        ? true
        : clientType === 'vendor'
          ? false
          : undefined,
    orders_from_date: (values.cfrom
      ? toUtcAdjusted(values.cfrom)
      : toUtcAdjusted().subtract(31, 'days')
    ).toISOString(),
    orders_till_date: (values.ctill
      ? toUtcAdjusted(values.ctill).add(1, 'day')
      : toUtcAdjusted().add(1, 'day')
    ).toISOString(),
    tariff_id: values.ctariff,
    sales_manager_id: values.cmanager,
  };
  if (sort) {
    const key = SORT_WIRE_KEY[sort.field];
    const ascending = sort.position === 1;
    if (key === 'order_by_created_at') filter.order_by_created_at = ascending;
    else if (key === 'order_by_order_price') filter.order_by_order_price = ascending;
    else if (key === 'order_by_order_count') filter.order_by_order_count = ascending;
    else if (key === 'order_by_balance') filter.order_by_balance = ascending;
    else if (key === 'order_by_balance_warning')
      filter.order_by_balance_warning = ascending;
    else filter.order_by_average_cheque = ascending;
  }
  if (values.op_from) filter.order_price_from = values.op_from;
  if (values.op_till) filter.order_price_till = values.op_till;
  if (values.oc_from) filter.order_count_from = values.oc_from;
  if (values.oc_till) filter.order_count_till = values.oc_till;
  if (values.bal_from) filter.balance_from = values.bal_from;
  if (values.bal_till) filter.balance_till = values.bal_till;
  if (clientType === 'business-vendor' && businessType !== 'ALL') {
    filter.is_corporate = businessType === 'CORPORATE';
  }
  if (clientType === 'business-vendor' && values.cstatus) {
    filter.status = values.cstatus;
  }
  return filter;
}

/** Default orders window shown by the filters modal (last 31 days → today). */
export function defaultOrdersWindow(): { from: string; till: string } {
  return {
    from: dayjs().subtract(31, 'days').format('YYYY-MM-DD'),
    till: dayjs().format('YYYY-MM-DD'),
  };
}
